import { CHARACTER_WHITESPACE, EMPTY_LIST } from '../../../constants/common'
import {
  filterPermittedActionsWithDividers,
  parseActionsOnValidDivider,
} from '../../../utils/massActions'

import {
  RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE,
  RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE_MASS,
  RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE,
  RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES,
  RENTAL_PORTAL_ARCHIVE_PAGE_SIZE,
  RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE,
  RENTAL_PORTAL_ARCHIVE_UNKNOWN_OBJECT_TYPE,
} from './RentalPortalArchive.constants'

function getIsRentalPortalArchiveListingItem(item) {
  return item.object_type === RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing
}

export function getIsRentalPortalArchiveActionRestoreMass(action) {
  return action === RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE_MASS
}

export function getIsRentalPortalArchiveActionRestore(action) {
  return action === RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE
}

export function getRentalPortalArchivePage({ allItems, page }) {
  const count = allItems.length
  const pageCount = Math.max(
    1,
    Math.ceil(count / RENTAL_PORTAL_ARCHIVE_PAGE_SIZE)
  )

  const safePage = Math.min(Math.max(page, 1), pageCount)
  const start = (safePage - 1) * RENTAL_PORTAL_ARCHIVE_PAGE_SIZE

  return {
    items: allItems.slice(start, start + RENTAL_PORTAL_ARCHIVE_PAGE_SIZE),
    pagination: {
      count,
      page: safePage,
      pageCount,
      pageSize: RENTAL_PORTAL_ARCHIVE_PAGE_SIZE,
    },
  }
}

export function getIsRentalPortalArchiveObjectTypeKnown(objectType) {
  return Boolean(
    RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES[objectType] &&
    RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE[objectType]
  )
}

export function getRentalPortalArchiveObjectType(objectType) {
  return (
    RENTAL_PORTAL_ARCHIVE_OBJECT_TYPES[objectType] ??
    RENTAL_PORTAL_ARCHIVE_UNKNOWN_OBJECT_TYPE
  )
}

export function getRentalPortalArchiveRestoreDescription({ item, t }) {
  return RENTAL_PORTAL_ARCHIVE_RESTORE_DESCRIPTION_KEYS_BY_TYPE[
    item.object_type
  ]
    .map(key => t(key, { name: item.object_name }))
    .join(CHARACTER_WHITESPACE)
}

export function getIsRentalPortalArchiveRestoreAllowed(item) {
  return Boolean(
    getIsRentalPortalArchiveObjectTypeKnown(item.object_type) &&
    item.permissions?.can_restore
  )
}

export function getRentalPortalArchiveMassActionItems({
  allItems,
  excludeIds,
  selectAllPages,
  selected,
}) {
  return selectAllPages
    ? allItems.filter(item => !excludeIds.includes(item.id))
    : selected
}

export function getRentalPortalArchiveListingItemsCount(items) {
  return items.filter(getIsRentalPortalArchiveListingItem).length
}

export function getRentalPortalArchiveMassActionEligibleItems(items) {
  return items.filter(getIsRentalPortalArchiveRestoreAllowed)
}

export function getRentalPortalArchiveMassActions({
  eligibleCount,
  selectedSize,
  t,
}) {
  const hasUnusableSelection = selectedSize > 0 && eligibleCount === 0

  const items = [
    {
      disabled: hasUnusableSelection,
      label: t('RentalPortal.ListingRestoreActionOptionKey'),
      permission: true,
      tooltipContent: hasUnusableSelection
        ? t('RentalPortal.ArchiveRestoreMassNotAllowedTooltipKey')
        : null,
      value: RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE_MASS,
    },
  ]

  return parseActionsOnValidDivider(filterPermittedActionsWithDividers(items))
}

export function getRentalPortalArchiveMassRestoreDescription({
  eligibleItems,
  selectedCount,
  t,
}) {
  const sentences = [
    eligibleItems.length === 1
      ? getRentalPortalArchiveRestoreDescription({
          item: eligibleItems[0],
          t,
        })
      : t('RentalPortal.ArchiveRestoreMassConfirmationTextKey', {
          count: eligibleItems.length,
        }),
    eligibleItems.length < selectedCount
      ? t('RentalPortal.ArchiveRestoreMassMixedEligibilityKey', {
          eligible: eligibleItems.length,
          selected: selectedCount,
        })
      : null,
    eligibleItems.length > 1 &&
    eligibleItems.some(
      item => item.object_type === RENTAL_PORTAL_ARCHIVE_OBJECT_TYPE.listing
    )
      ? t('RentalPortal.ListingRestoreNoRepublishNoticeKey')
      : null,
  ]

  return sentences.filter(Boolean).join(CHARACTER_WHITESPACE)
}

export function getRentalPortalArchiveItemActions(item) {
  const isRestoreAllowed = getIsRentalPortalArchiveRestoreAllowed(item)

  return getIsRentalPortalArchiveObjectTypeKnown(item.object_type)
    ? [
        {
          disabled: !isRestoreAllowed,
          label: 'RentalPortal.ListingRestoreActionOptionKey',
          tooltipContent: isRestoreAllowed
            ? null
            : getRentalPortalArchiveObjectType(item.object_type)
                .restoreNotAllowedTooltip,
          value: RENTAL_PORTAL_ARCHIVE_ACTION_RESTORE,
        },
      ]
    : EMPTY_LIST
}
